import { useDispatch, useSelector } from "react-redux"
import { useEffect, useRef, useState } from "react"
import { useCreateSbpOutcomeTransfer } from "../../../api/hooks/useCreateSbpOutcomeTransfer"
import { useConfirmSbpOutcomeTransfer } from "../../../api/hooks/useConfirmSbpOutcomeTransfer"

import {
  IBankMeta,
  BankRole,
  BankScenario,
  ICreateSbpOutcomeTransferRequest,
  OperationStatus,
} from "../../../api/interface"

import { RootState } from "../../../store/store"
import {
  setSelectedBank,
  setBankVariant,
  IRecipient,
  setRecipient,
  setDefaultBank,
} from "../../../store/slices/c2cSlice"
import { setNextStep } from "../../../store/slices/routingSlice/routingSlice"

import BankSelection from "../../../pages/BankSelection"
import CreateSbpOutcomeTransfer from "../../../pages/SbpOutcomeTransfer/Create"
import ConfirmSbpOutcomeTransfer from "../../../pages/SbpOutcomeTransfer/Confirm"
import SbpOutcomeTransferStatus from "../../../pages/SbpOutcomeTransfer/Status"
import ContactsTransfersHistory from "../../../pages/ContactsTransfersHistory"
import ContactsList from "../../../pages/ContactsList"

import {
  STEPS_NAME,
  c2cPushSteps,
} from "../../../store/slices/routingSlice/flowsSteps"
import { formatPhoneValidation } from "../../../utils/formatters"
import { useGetDefaultBankByPhoneNumber } from "../../../api/hooks/useDefaultBank"
import { useGetSbpOutcomeTransferStatus } from "../../../api/hooks/useGetSbpOutcomeTransferStatus"
import ErrorHandlerModal from "../../../components/Modal/ErrorHandlerModal"
import SbpOutcomeTransferError from "../../../pages/SbpOutcomeTransfer/Error"
import { getFingerPrint } from "../../../utils/fingerprint"

// перевод клиенту СБП
const C2CPushFlow = () => {
  const dispatch = useDispatch()
  const [openError, setOpenError] = useState(false)
  const [enablePolling, setEnablePolling] = useState(false)
  const [isLoadingTransferStatus, setIsLoadingTransferStatus] = useState(false)
  const transferForm = useSelector((state: RootState) => state.transferForm)
  const transferStep = useSelector((state: RootState) => state.routing.routes.c2cPush.currentStep)
  const transferStepRef = useRef(transferStep)

  const {
    mutate: createOutcomeTransfer,
    data: createOutcomeTransferResponse,
    isPending: isOutcomeTransferCreating,
    error: createOutcomeTransferError,
    isSuccess: isOutcomeTransferCreated,
  } = useCreateSbpOutcomeTransfer()

  const { mutate: confirmOutcomeTransfer, isPending: isOutcomeTransferConfirming } = useConfirmSbpOutcomeTransfer()
  const { mutate: getDefaultBank, isPending: isDefaultBankLoading } = useGetDefaultBankByPhoneNumber()
  const { data: getOutcomeTransferResponse } = useGetSbpOutcomeTransferStatus(createOutcomeTransferResponse?.outcomeTransferId, enablePolling)

  const handleSelectContact = async (contact: IRecipient) => {
    getDefaultBank(contact.phone, {
      onSuccess: (dataDefault) => {
        if (dataDefault && !isDefaultBankLoading) {
          dispatch(setRecipient(contact))
          dispatch(setSelectedBank(dataDefault))
          dispatch(setDefaultBank(dataDefault))
          dispatch(setNextStep())
        } else {
          dispatch(setRecipient(contact))
          dispatch(setNextStep(STEPS_NAME.c2cPush.bankSelectionTransferC2C))
        }
      },
    })
  }

  const handleClickNext = async () => {
    getDefaultBank(transferForm.recipient?.phone, {
      onSuccess: (dataDefault) => {
        if (dataDefault && !isDefaultBankLoading) {
          dispatch(setSelectedBank(dataDefault))
          dispatch(setDefaultBank(dataDefault))
          dispatch(setNextStep())
        } else {
          dispatch(setNextStep())
        }
      },
    })
  }

  const handleBankClick = (bank: IBankMeta) => {
    dispatch(setBankVariant(bank))
    dispatch(setSelectedBank(bank))
    dispatch(setNextStep(STEPS_NAME.c2cPush.createTransfer))
  }

  const handleTransferTimeout = () => {
    if (transferStepRef.current === c2cPushSteps.confirmTransfer.name) {
      dispatch(setNextStep(STEPS_NAME.c2cPush.transferStatus))
    }
  }

  const handleCreateOutcomeTransfer = () => {
    const fingerprint = getFingerPrint(false)

    const reqData: ICreateSbpOutcomeTransferRequest = {
      phoneNumber: formatPhoneValidation(transferForm.recipient!.phone),
      bankId: transferForm.selectedBank!.bankId,
      amount: Number(transferForm.amount.value.replace(",", ".")),
      description: transferForm.paymentPurpose,
      fingerprint,
    }

    createOutcomeTransfer(reqData)
    setIsLoadingTransferStatus(true)
    setTimeout(() => handleTransferTimeout(), 10000)
  }

  const handleConfirmOutcomeTransfer = () => {
    if (createOutcomeTransferError) {
      // TODO: тут ошибка, невозможно получить статус трансфера без его идентификатора
      dispatch(setNextStep(STEPS_NAME.c2cPush.transferStatus))
    } else if (createOutcomeTransferResponse?.outcomeTransferId) {
      confirmOutcomeTransfer(createOutcomeTransferResponse?.outcomeTransferId)
      setEnablePolling(true)
      setIsLoadingTransferStatus(true)
      setTimeout(() => handleTransferTimeout(), 10000)
    }
  }

  

  const handleGetOutcomeTransfer = () => {
    setEnablePolling(false)
    setIsLoadingTransferStatus(false)
  }

  useEffect(() => {
    setOpenError(!!createOutcomeTransferError)
  }, [createOutcomeTransferError])

  useEffect(() => {
    if (createOutcomeTransferResponse?.outcomeTransferId) {
      setEnablePolling(true)

      switch (getOutcomeTransferResponse?.status) {
        case OperationStatus.CREATE_ERROR:
          handleGetOutcomeTransfer()
          dispatch(setNextStep(c2cPushSteps.transferError.name))
          break

        case OperationStatus.CREATED:
          handleGetOutcomeTransfer()
          if (transferStepRef.current !== c2cPushSteps.confirmTransfer.name) {
            dispatch(setNextStep())
          }
          break

        case OperationStatus.ACCEPTED:
          handleGetOutcomeTransfer()
          dispatch(setNextStep())
          break

        case OperationStatus.ACCEPT_ERROR:
          handleGetOutcomeTransfer()
          dispatch(setNextStep())
          break

        default:
          break
      }
    }
  }, [getOutcomeTransferResponse, createOutcomeTransferResponse?.outcomeTransferId])

  const defaultBank = useSelector(
    (state: RootState) => state.transferForm.defaultBank,
  )

  switch (transferStep) {
    case c2cPushSteps.getLastTransfers.name: {
      return (
        <ContactsTransfersHistory
          handleSelectContact={handleSelectContact}
          isLoading={isDefaultBankLoading}
          handleClickNext={handleClickNext}
        />
      )
    }
    case c2cPushSteps.getContacts.name: {
      return (
        <ContactsList
          handleSelectContact={handleSelectContact}
          isLoading={isDefaultBankLoading}
          handleClickNext={handleClickNext}
        />
      )
    }
    case c2cPushSteps.createTransfer.name: {
      return (
        <>
          <CreateSbpOutcomeTransfer
            handleCreate={handleCreateOutcomeTransfer}
            isLoading={isOutcomeTransferCreating || isLoadingTransferStatus}
          />
          <ErrorHandlerModal
            open={openError}
            toggleMOdal={() => {
              setOpenError(false)
              setIsLoadingTransferStatus(false)
            }}
          />
        </>
      )
    }
    case c2cPushSteps.confirmTransfer.name: {
      return isOutcomeTransferCreated ? (
        <ConfirmSbpOutcomeTransfer
          pam={getOutcomeTransferResponse?.recipientInfo.pam as string}
          commissionAmount={getOutcomeTransferResponse?.commissionAmount!}
          handleConfirm={handleConfirmOutcomeTransfer}
          isLoading={isOutcomeTransferCreating || isLoadingTransferStatus}
        />
      ) : null
    }
    case c2cPushSteps.transferStatus.name: {
      return (
        <SbpOutcomeTransferStatus
          setEnablePolling={setEnablePolling}
          enablePolling={enablePolling}
          outcomeTransferId={createOutcomeTransferResponse?.outcomeTransferId}
          isLoading={isOutcomeTransferCreating || isOutcomeTransferConfirming}
        />
      )
    }
    case c2cPushSteps.transferError.name: {
      return (
        <SbpOutcomeTransferError/>
      )
    }
    case c2cPushSteps.bankSelectionPhone.name: {
      return (
        <BankSelection
          handleBankClick={handleBankClick}
          role={BankRole.RECEIVER}
          scenario={BankScenario.C2C_PUSH}
        />
      )
    }
    case c2cPushSteps.bankSelectionContacts.name: {
      return (
        <BankSelection
          handleBankClick={handleBankClick}
          role={BankRole.RECEIVER}
          scenario={BankScenario.C2C_PUSH}
        />
      )
    }
    case c2cPushSteps.bankSelectionTransferC2C.name: {
      return (
        <BankSelection
          handleBankClick={handleBankClick}
          defaultBank={defaultBank}
          role={BankRole.RECEIVER}
          scenario={BankScenario.C2C_PUSH}
        />
      )
    }
    default: {
      return (
        <ContactsTransfersHistory
          handleSelectContact={handleSelectContact}
          isLoading={isDefaultBankLoading}
          handleClickNext={handleClickNext}
        />
      )
    }
  }
}

export default C2CPushFlow
